<template>
    <div>
        <activix-modal
            classes="sms-modal"
            size="lg"
            cancel-class-name="mt-2 z-20 | xl:mt-6"
            :opened="opened"
            :loading="loading"
            @open="onOpen"
            @close="close"
            @closed="onClosed"
        >
            <template #body>
                <iframe id="niotext" class="h-full" scrolling="no" :src="nioTextUrl" />
            </template>
        </activix-modal>

        <activix-confirm-modal
            type="info"
            :content="communicatingModalConfirm.text"
            :approve-text="$t('general.yes')"
            :deny-text="$t('general.cancel')"
            :show-deny="false"
            @cancel="communicatingModalConfirm.text = ''"
            v-model:opened="communicatingModalConfirm.opened"
        />
    </div>
</template>

<script>
    import { mapActions } from 'pinia';
    import { useGlobalStore } from '@/store/store.ts';
    import { useContextStore } from '@/store/modules/context/store.ts';
    import { useUserStore } from '@/store/modules/user/store.js';
    import { useTimedPageAction } from '@/hooks/useTimedPageAction.ts';
    import TimedPageActions from '@/constants/TimedPageActions.ts';

    import { showWarning } from '@/utils/toastr.js';

    import LeadPhoneValidation from '@/mixins/LeadPhoneValidation.js';

    import CommunicationMethod from '@/entities/CommunicationMethod.js';
    import Lead from '@/entities/Lead.js';
    import { useEventBus } from '@/hooks/useEventBus.ts';

    const { emit } = useEventBus();

    export const SmsModalBlockedReason = {
        MISSING_NIOTEXT_OR_COMMUNICATIONS: 'missing_niotext_or_communications',
        NIOTEXT_LIMIT_EXCEEDED: 'niotext_limit_exceeded',
        NO_VALID_PHONE: 'no_valid_phone',
        NIOTEXT_ERROR: 'niotext_error',
        MISSING_NIOTEXT_OR_VALIDATING: 'missing_niotext_or_validating',
        UNSUBSCRIBED: 'unsubscribed',
        NO_UNVALIDATED_PHONE: 'no_unvalidated_phone',
        MISSING_NIOTEXT: 'missing_niotext',
    };

    export default {
        mixins: [LeadPhoneValidation],

        setup() {
            const eventBus = useEventBus();
            return { eventBus };
        },

        data() {
            return {
                lead: new Lead(),
                leadId: null,
                loggedIn: false,
                loading: true,
                nioTextIframe: null,
                communicationUserId: null,
                niotextId: null,
                communicationId: null,
                opened: false,
                communicatingModalConfirm: {
                    opened: false,
                    text: '',
                },
                triggerSource: null,
                niotextTimer: null,
                niotextTimerMarked: false,
            };
        },

        computed: {
            parentAuthUser() {
                return useUserStore().parentAuthUser;
            },

            authUser() {
                return useUserStore().authUser;
            },

            nioTextUrl() {
                return process.env.VUE_APP_NIOTEXT_URL;
            },

            authUserHasNiotextForLead() {
                return this.parentAuthUser.selfOrChildForAccountHasNioText(this.lead.account_id);
            },
        },

        methods: {
            ...mapActions(useGlobalStore, ['appendNewError', 'fetchLead']),

            emitSmsModalBlocked(reason) {
                emit('sms-modal-blocked', {
                    leadId: this.leadId || this.lead?.id,
                    reason,
                });
            },

            async removeCommunicating() {
                if (!this.authUserHasNiotextForLead) {
                    return;
                }

                await this.$api.leads.removeCommunicating({ id: this.leadId });
            },

            async setCommunicating(leadId) {
                if (!this.authUserHasNiotextForLead || !leadId) {
                    return;
                }

                const response = await this.$api.leads.setCommunicating(
                    { id: leadId },
                    { type: CommunicationMethod.SMS }
                );
                const { data } = response.data;

                if (empty(data.user)) {
                    this.opened = true;
                    return;
                }

                let communicatingText = 'sendingEmail';

                if (data.type == CommunicationMethod.PHONE) {
                    communicatingText = 'calling';
                } else if (data.type == CommunicationMethod.SMS) {
                    communicatingText = 'sendingSms';
                }

                this.communicatingModalConfirm.text = this.$t(
                    `responseLeadXpress.${communicatingText}`,
                    [this.getFullName(data.user)]
                );

                this.communicatingModalConfirm.opened = true;
            },

            initIframe() {
                this.nioTextIframe = iFrameResize(
                    {
                        checkOrigin: [process.env.VUE_APP_NIOTEXT_URL],
                        sizeHeight: false,
                        scrolling: true,
                        onMessage: (data) => {
                            if (data.message == 'ready') {
                                this.sendInfo();
                            }

                            if (data.message == 'leadSelected') {
                                this.loading = false;
                                this.markNiotextLoadSuccess();
                            }
                        },
                    },
                    '#niotext'
                )[0].iFrameResizer;
            },

            sendInfo() {
                this.nioTextIframe.sendMessage({
                    accessToken: this.parentAuthUser.niotext_token,
                    accountId: this.lead.account_id,
                    leadId: this.lead.id,
                    contactId: this.niotextId,
                    userId: this.communicationUserId || this.parentAuthUser.id,
                });
            },

            async openModal() {
                if (!this.authUserHasNiotextForLead || !this.lead.communications) {
                    this.emitSmsModalBlocked(
                        SmsModalBlockedReason.MISSING_NIOTEXT_OR_COMMUNICATIONS
                    );
                    return;
                }

                // Opened from communication list
                if (this.communicationUserId) {
                    this.niotextId = this.lead.niotext_id;
                    this.opened = true;
                    return;
                }

                try {
                    const response = await this.$axios.post(
                        `v1/leads/${this.lead.id}/query-niotext`
                    );

                    this.niotextId = response.data.data.niotext_id;
                    this.setCommunicating(this.lead.id);
                } catch (error) {
                    if (
                        error.response &&
                        error.response.status === 403 &&
                        error.response.message === 'Limit exceeded.'
                    ) {
                        showWarning(this.$t('toastr.noPhoneNioText'));
                        this.emitSmsModalBlocked(SmsModalBlockedReason.NIOTEXT_LIMIT_EXCEEDED);
                        return;
                    }

                    if (
                        error.response &&
                        error.response.status === 403 &&
                        error.response.message === 'No valid phone number for this contact.'
                    ) {
                        showWarning(this.$t('toastr.noMobilePhone'));
                        this.emitSmsModalBlocked(SmsModalBlockedReason.NO_VALID_PHONE);
                        return;
                    }

                    showWarning(this.$t('toastr.errorMessages.niotext'));

                    this.appendNewError({
                        code: '0097',
                        display: false,
                        error,
                    });
                    this.emitSmsModalBlocked(SmsModalBlockedReason.NIOTEXT_ERROR);
                }
            },

            async triggerOpenModal({ leadId, communication = null, triggerSource = null }) {
                this.triggerSource = triggerSource;
                this.leadId = leadId;
                this.communicationId = communication?.id || null;
                this.lead = await this.fetchLead(leadId);

                if (!this.authUserHasNiotextForLead || this.$wait.is('validating.leadPhones')) {
                    this.emitSmsModalBlocked(SmsModalBlockedReason.MISSING_NIOTEXT_OR_VALIDATING);
                    return;
                }

                // Make sure lead can be contacted
                if (this.lead.unsubscribe_all_date) {
                    this.emitSmsModalBlocked(SmsModalBlockedReason.UNSUBSCRIBED);
                    return;
                }

                if (communication && communication.user) {
                    this.communicationUserId =
                        communication.user.parent_user_id || communication.user.id;
                }

                // Clicked on communication, or already validated. Skip validation.
                if (communication || this.hasValidatedMobile(this.lead)) {
                    this.openModal();
                    return;
                }
                // All phones are already validated...
                if (!this.hasNotValidatedPhone(this.lead)) {
                    this.emitSmsModalBlocked(SmsModalBlockedReason.NO_UNVALIDATED_PHONE);
                    return;
                }

                this.validatePhones();
            },

            async validatePhones() {
                this.$wait.start('validating.leadPhones');

                const validPhones = await useContextStore().validateLeadPhones(this.leadId);
                if (validPhones) {
                    this.openModal();
                } else {
                    showWarning(this.$t('client.noMobilePhone'));
                    this.emitSmsModalBlocked(SmsModalBlockedReason.NO_VALID_PHONE);
                }

                this.$wait.end('validating.leadPhones');
            },

            close() {
                this.opened = false;
            },

            onOpen() {
                if (!this.authUserHasNiotextForLead) {
                    this.emitSmsModalBlocked(SmsModalBlockedReason.MISSING_NIOTEXT);
                    return;
                }
                this.startNiotextTimer();
                this.initIframe();
            },
            onClosed() {
                if (!this.niotextTimerMarked) {
                    this.markNiotextLoadFailure('niotext_load_cancelled');
                }
                this.removeCommunicating();
                this.loading = true;
                this.communicationUserId = null;
                this.niotextTimerMarked = false;

                if (this.nioTextIframe) {
                    this.nioTextIframe.close();
                    this.nioTextIframe = null;
                }

                this.$behavior.track('OpenCommunication', {
                    Page: 'Lead',
                    Action: 'close',
                    ElementName: 'sms',
                    Location: this.triggerSource,
                    LeadId: this.lead.id,
                    LeadSource: this.lead.source?.name,
                    LeadProvider: this.lead.source?.provider?.name,
                    TenantId: this.lead.account_id,
                    TenantName: this.lead.account?.name,
                });

                this.lead = new Lead();
                this.leadId = null;
                this.communicationId = null;
                this.triggerSource = null;
            },

            startNiotextTimer() {
                this.niotextTimer = useTimedPageAction({
                    action: TimedPageActions.NIOTEXT_LOAD_MODAL,
                    additionalData: {},
                });
                this.niotextTimerMarked = false;
            },

            markNiotextLoadSuccess() {
                if (!this.niotextTimer || this.niotextTimerMarked) {
                    return;
                }

                this.niotextTimer.markSuccess();
                this.niotextTimerMarked = true;
                this.niotextTimer = null;
                emit('sms-history-loaded', {
                    leadId: this.leadId || this.lead?.id,
                    communicationId: this.communicationId,
                });
            },

            markNiotextLoadFailure(reason) {
                if (!this.niotextTimer || this.niotextTimerMarked) {
                    return;
                }

                try {
                    this.niotextTimer.markFailure(new Error(reason || 'niotext_load_failure'));
                } catch (error) {
                    /**
                     * markFailure may throw if the timer has already been marked as completed
                     * should not prevent the modal from closing.
                     * Swallowing this error is safe here because failure to record telemetry
                     * should not prevent the modal from closing.
                     * */
                } finally {
                    this.niotextTimerMarked = true;
                    this.niotextTimer = null;
                }
            },
        },

        created() {
            this.eventBus.on('open-sms-modal', this.triggerOpenModal);
        },

        beforeUnmount() {
            this.eventBus.off('open-sms-modal', this.triggerOpenModal);

            this.$wait.end('validating.leadPhones');
        },
    };
</script>
